import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Plug,
  Zap,
  RefreshCw,
  Package,
  Truck,
  ShoppingBag,
  Layers,
  CheckCircle2,
  Clock,
  ShieldCheck,
  MessageCircle,
} from 'lucide-react';
import { SiShopify, SiTiktok, SiWalmart, SiWoocommerce, SiEtsy, SiSquarespace, SiWix, SiEbay } from 'react-icons/si';
import { FaAmazon } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import SEO from '../seo/SEO';

const LIVE = [
  {
    name: 'Shopify',
    Logo: SiShopify,
    blurb:
      'The cleanest, most-tested integration in our stack. Connect your Shopify store in minutes and your orders flow into our system automatically.',
    syncs: [
      'Orders sync within seconds of checkout',
      'Inventory levels updated in real time',
      'Tracking numbers pushed back to Shopify',
      'Returns synced when received & inspected',
      'Multi-location support for split-fulfillment',
    ],
    setup: '~5 minutes',
    bg: 'linear-gradient(135deg, #00875A 0%, #008060 50%, #95BF47 100%)',
    accent: '#008060',
  },
  {
    name: 'TikTok Shop',
    Logo: SiTiktok,
    blurb:
      'Native TikTok Shop fulfillment. Whether your orders come from a viral live, an in-feed ad, or a creator post, they hit our dock the same way.',
    syncs: [
      'Orders sync from TikTok Shop in real time',
      'Same-day shipping for orders before cutoff',
      'Tracking auto-uploaded to meet TikTok\u2019s SLA',
      'Inventory shared across TikTok and other channels',
      'Returns processed per TikTok Shop policy',
    ],
    setup: '~10 minutes',
    bg: 'linear-gradient(135deg, #010101 0%, #25F4EE 60%, #FE2C55 100%)',
    accent: '#FE2C55',
  },
  {
    name: 'Amazon Seller Central',
    Logo: FaAmazon,
    blurb:
      'FBM fulfillment from our warehouse. Ship MFN orders within Amazon\u2019s SLA without juggling FBA inbound rules \u2014 or run as a backup when FBA is constrained.',
    syncs: [
      'MFN orders pulled from Seller Central',
      'On-time, valid tracking confirmed back to Amazon',
      'Buy Box-friendly delivery promises by zone',
      'Inventory sync to prevent oversells',
      'FBA prep available as a sister workflow',
    ],
    setup: '~15 minutes',
    bg: 'linear-gradient(135deg, #232F3E 0%, #131A22 50%, #FF9900 100%)',
    accent: '#FF9900',
  },
];

const COMING_SOON = [
  { name: 'WooCommerce', Logo: SiWoocommerce, color: '#96588A', size: '2.5rem' },
  { name: 'Etsy', Logo: SiEtsy, color: '#F16521', size: '1.75rem' },
  { name: 'Walmart Marketplace', Logo: SiWalmart, color: '#0071CE', size: '2rem' },
  { name: 'Squarespace', Logo: SiSquarespace, color: '#000000', size: '1.75rem' },
  { name: 'Wix', Logo: SiWix, color: '#0C6EFC', size: '2rem' },
  { name: 'eBay', Logo: SiEbay, color: '#E53238', size: '2.25rem' },
];

const FLOW_STEPS = [
  {
    step: 1,
    icon: ShoppingBag,
    title: 'Customer places an order',
    body: 'A buyer checks out on Shopify, TikTok Shop, or Amazon. Their order details and address are captured.',
  },
  {
    step: 2,
    icon: RefreshCw,
    title: 'Order syncs to Shipmaster',
    body: 'The order is pushed to our system within seconds via the platform\u2019s native API \u2014 no manual exports, no spreadsheets.',
  },
  {
    step: 3,
    icon: Package,
    title: 'We pick, pack & ship',
    body: 'Same-day if it beats our 1pm ET cutoff. Picked from your inventory, packed in your branded packaging, labeled, and tendered to UPS / FedEx / USPS.',
  },
  {
    step: 4,
    icon: Truck,
    title: 'Tracking flows back',
    body: 'Carrier tracking is automatically posted to the storefront so the customer gets a confirmation email and your team has a clean audit trail.',
  },
];

const SYNC_FEATURES = [
  {
    icon: Zap,
    title: 'Order data, in seconds',
    body: 'New orders hit our queue almost the instant a customer checks out. No batch jobs, no end-of-day pulls.',
  },
  {
    icon: Layers,
    title: 'Inventory across channels',
    body: 'One source of truth for stock. A unit sold on Shopify drops the count on TikTok Shop and Amazon at the same time.',
  },
  {
    icon: Truck,
    title: 'Tracking, automated',
    body: 'Tracking numbers and carrier IDs are written back to the platform so customers get confirmation emails without you lifting a finger.',
  },
  {
    icon: ShieldCheck,
    title: 'Returns, reconciled',
    body: 'When a return arrives we inspect, disposition, and update the storefront so refunds and exchanges stay tidy.',
  },
];

export default function Integrations() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title="Shopify, TikTok Shop & Amazon Fulfillment Integrations"
        description="Native 3PL integrations for Shopify, TikTok Shop, and Amazon Seller Central. Real-time order, inventory, and tracking sync — no spreadsheets, no Zapier middleware."
        keywords="Shopify 3PL integration, TikTok Shop fulfillment, Amazon FBM 3PL, ecommerce platform integrations, Shopify order sync, real-time inventory sync"
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-16 sm:pt-20 pb-12 overflow-hidden">
        <div className="absolute inset-0 -z-10 brand-soft-bg" />
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute -top-40 -right-32 w-[480px] h-[480px] rounded-full bg-indigo-200/40 blur-3xl" />
          <div className="absolute top-32 -left-32 w-[420px] h-[420px] rounded-full bg-blue-200/30 blur-3xl" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-indigo-100 text-indigo-700 text-xs font-bold uppercase tracking-widest shadow-sm">
            <Plug className="w-3.5 h-3.5" /> Integrations
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Connect once.{' '}
            <span className="brand-gradient-text">Ship everywhere.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto">
            Native integrations with the channels your customers actually buy from. No middleware,
            no clunky Zapier chains, no spreadsheets at 2am.
          </p>
        </div>
      </section>

      {/* Live integrations */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-indigo-600">Live</p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
                Available today
              </h2>
            </div>
            <p className="text-sm text-slate-500 max-w-md">
              Three native integrations cover the bulk of modern ecommerce volume. Each takes
              minutes to set up.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {LIVE.map((p) => (
              <article
                key={p.name}
                className="group rounded-3xl overflow-hidden border border-slate-200 bg-white hover:shadow-xl hover:border-indigo-200 transition-all duration-200 flex flex-col"
              >
                <div
                  className="relative p-7 sm:p-8 text-white overflow-hidden"
                  style={{ background: p.bg }}
                >
                  <div
                    className="absolute inset-0 opacity-15 pointer-events-none"
                    style={{
                      backgroundImage:
                        'repeating-linear-gradient(135deg, rgba(255,255,255,0.4) 0px, rgba(255,255,255,0.4) 1px, transparent 1px, transparent 22px)',
                    }}
                    aria-hidden="true"
                  />
                  <div className="relative">
                    <p.Logo
                      aria-label={`${p.name} logo`}
                      className="h-9 w-auto text-white"
                      style={{ height: '2.25rem', width: 'auto' }}
                    />
                    <h3 className="mt-5 text-2xl font-extrabold tracking-tight">{p.name}</h3>
                    <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm text-xs font-bold uppercase tracking-widest">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Live
                    </div>
                  </div>
                </div>

                <div className="flex-1 p-7 flex flex-col">
                  <p className="text-slate-700 leading-relaxed">{p.blurb}</p>

                  <p className="mt-6 text-xs font-bold uppercase tracking-widest text-slate-500">
                    What syncs
                  </p>
                  <ul className="mt-3 space-y-2">
                    {p.syncs.map((s) => (
                      <li key={s} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <span className="flex-shrink-0 w-4 h-4 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5">
                          <CheckCircle2 className="w-3 h-3 text-indigo-600" strokeWidth={3} />
                        </span>
                        {s}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto pt-5 border-t border-slate-100 flex items-center gap-2 text-sm text-slate-500 font-medium">
                    <Clock className="w-4 h-4" />
                    Setup during onboarding · {p.setup}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How it flows */}
      <section className="py-20 sm:py-24 bg-slate-50/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-indigo-600">
              How it flows
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
              From checkout to doorstep.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Same flow regardless of channel. The platform changes; the operation doesn&apos;t.
            </p>
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Connector line */}
            <div className="hidden md:block absolute top-[44px] left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-indigo-200 via-indigo-300 to-indigo-200" />

            {FLOW_STEPS.map((s) => (
              <div key={s.step} className="relative">
                <div className="flex justify-center">
                  <div className="relative z-10 w-[88px] h-[88px] rounded-full bg-white border-2 border-indigo-100 flex items-center justify-center shadow-sm">
                    <div className="w-16 h-16 rounded-full brand-gradient flex items-center justify-center">
                      <s.icon className="w-7 h-7 text-white" strokeWidth={2} />
                    </div>
                  </div>
                </div>
                <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-6 text-center hover:border-indigo-200 hover:shadow-md transition-all duration-200">
                  <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
                    Step {s.step}
                  </p>
                  <h3 className="mt-1.5 text-lg font-bold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What syncs */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
            <div className="lg:col-span-1">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-indigo-600">
                Real-time sync
              </p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
                Data flows both ways. Always current.
              </h2>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                Every integration uses the platform&apos;s native API for the cleanest, fastest
                handoff. You&apos;ll never reconcile a CSV at midnight.
              </p>
            </div>
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {SYNC_FEATURES.map((f) => (
                <div
                  key={f.title}
                  className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-indigo-200 hover:shadow-md transition-all duration-200"
                >
                  <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center mb-4">
                    <f.icon className="w-5 h-5 text-indigo-600" strokeWidth={2} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1.5">{f.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Coming soon / on request */}
      <section className="py-20 sm:py-24 bg-slate-50/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-indigo-600">
              On request
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Don&apos;t see your platform?
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              We can wire up most ecommerce platforms via API, EDI, or scheduled file drop. Tell
              us what you sell on and we&apos;ll tell you what&apos;s realistic.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {COMING_SOON.map((p) => (
              <div
                key={p.name}
                className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col items-center gap-3 hover:border-indigo-200 hover:shadow-sm transition-all duration-200"
              >
                <div className="h-10 flex items-center justify-center">
                  <p.Logo
                    aria-label={p.name}
                    className="opacity-80"
                    style={{ fontSize: p.size, color: p.color }}
                  />
                </div>
                <p className="text-xs font-semibold text-slate-700 text-center leading-tight">
                  {p.name}
                </p>
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  On request
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-indigo-50 border border-indigo-100 rounded-3xl p-10 sm:p-14 text-center">
            <div
              className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-indigo-200/40 blur-3xl pointer-events-none"
              aria-hidden="true"
            />
            <div className="relative">
              <div className="w-14 h-14 mx-auto rounded-2xl brand-gradient flex items-center justify-center mb-5 shadow-lg shadow-indigo-500/20">
                <Plug className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
                Ready to plug in?
              </h2>
              <p className="mt-3 text-lg text-slate-600 max-w-xl mx-auto">
                Most stores are live the same week. Talk to a real operator and we&apos;ll map
                your stack in 15 minutes.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  asChild
                  size="lg"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-12 px-7 text-base font-semibold shadow-md hover:shadow-lg transition-all duration-200 group"
                >
                  <Link to="/#contact" className="inline-flex items-center gap-2">
                    Book a Call
                    <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="rounded-full h-12 px-7 border-slate-300 text-slate-800 hover:border-indigo-500 hover:text-indigo-600 transition-colors"
                >
                  <Link to="/zones#instant-estimate" className="inline-flex items-center gap-2">
                    Get Instant Estimate
                  </Link>
                </Button>
              </div>

              <p className="mt-6 text-sm text-slate-500 inline-flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> Or email{' '}
                <a
                  href="mailto:info@shipmasterprep.com"
                  className="text-indigo-600 font-semibold hover:underline"
                >
                  info@shipmasterprep.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
